"use client"

import { useEffect, type ReactNode } from "react"
import { SWRConfig } from "swr"
import type { Cache } from "swr"

const CACHE_STORAGE_KEY = "ort-swr-cache"

// Holds the live cache Map so the persist effect can serialize it on unload.
const cacheRef: { current: Map<string, unknown> | null } = { current: null }

/** Shared fetcher: GET JSON, throw on non-2xx so SWR surfaces the error.
 *  Exported so `preload()` (hover/idle warming) uses the same fetcher. */
export async function jsonFetcher(url: string) {
  const response = await fetch(url, { cache: "no-store" })
  if (!response.ok) throw new Error(`Request failed (HTTP ${response.status}).`)
  return response.json()
}

/**
 * Build the SWR cache Map, seeded from sessionStorage so the last-seen data
 * (e.g. the agent roster) paints instantly on a same-tab reload instead of
 * showing a spinner. Within a live SPA session the in-memory Map already
 * survives soft navigation; this extends that across hard reloads in the same
 * tab. sessionStorage (not localStorage) keeps it scoped to the tab session and
 * out of long-lived storage.
 */
function readPersistedCache(): Map<string, unknown> {
  if (typeof window === "undefined") return new Map()
  try {
    const raw = sessionStorage.getItem(CACHE_STORAGE_KEY)
    return new Map(raw ? JSON.parse(raw) : [])
  } catch {
    // Corrupt/unavailable storage must never break startup — start empty.
    return new Map()
  }
}

// Stable config object (module scope) so the SWR context value identity never
// changes across renders — otherwise every render re-propagates context to all
// useSWR consumers.
const SWR_CONFIG = {
  fetcher: jsonFetcher,
  revalidateOnFocus: true,
  keepPreviousData: true,
  provider: () => {
    const map = readPersistedCache()
    cacheRef.current = map
    return map as unknown as Cache
  },
}

export function SwrProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    // Persist the cache on tab hide so a same-tab reload starts warm. Only on
    // `pagehide` — NOT on unmount — so React Strict Mode's mount/unmount cycle
    // can't overwrite warm sessionStorage with the initial (empty) cache.
    const persist = () => {
      const map = cacheRef.current
      if (!map) return
      try {
        sessionStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(Array.from(map.entries())))
      } catch {
        // Best-effort; quota/serialization failures must never break the app.
      }
    }
    window.addEventListener("pagehide", persist)
    return () => window.removeEventListener("pagehide", persist)
  }, [])

  return <SWRConfig value={SWR_CONFIG}>{children}</SWRConfig>
}
